import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { Button } from 'primeng/button';

import { TranslationModule } from '@core/translation/translation.module';
import { U2bDialogComponent } from './u2b-dialog.component';

export type U2bConfirmSeverity = 'primary' | 'warn' | 'danger';

/**
 * Confirmation modal built on `u2b-dialog`: a backdrop, a colored header
 * (severity variant), a message and Cancel/Confirm actions. Presentation only —
 * the caller mounts it (CDK overlay / DynamicDialog) and reacts to
 * `confirmed`/`cancelled`. All labels are Tolgee keys.
 *
 * Header colors are set in SCSS (not Tailwind utilities) so the variant renders
 * regardless of JIT class generation.
 */
@Component({
    selector: 'u2b-confirm-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Button, TranslationModule, U2bDialogComponent],
    host: {
        class: 'fixed inset-0 z-[20000] bg-black/60 flex items-center justify-center p-4',
        role: 'dialog',
        'aria-modal': 'true',
        '[class.u2b-confirm--warn]': "severity() === 'warn'",
        '[class.u2b-confirm--danger]': "severity() === 'danger'",
        '(click)': 'cancelled.emit()',
        '(document:keydown.escape)': 'cancelled.emit()',
    },
    template: `
        <u2b-dialog
            class="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            (click)="$event.stopPropagation()">

            <div u2bDialogHeader class="flex items-center gap-3">
                @if (icon(); as ic) {
                    <span class="material-icons" aria-hidden="true">{{ ic }}</span>
                }
                @if (titleKey(); as key) {
                    <h2 class="text-lg font-semibold m-0">{{ key | translate }}</h2>
                }
            </div>

            <p class="m-0 text-p-text leading-relaxed">{{ messageKey() | translate: messageParams() }}</p>

            <div u2bDialogFooter class="flex items-center gap-2 ml-auto">
                <p-button
                    severity="secondary"
                    [text]="true"
                    [label]="cancelKey() | translate"
                    (onClick)="cancelled.emit()" />
                @if (declineKey(); as key) {
                    <p-button
                        severity="secondary"
                        [label]="key | translate"
                        (onClick)="declined.emit()" />
                }
                <p-button
                    [severity]="confirmSeverity()"
                    [label]="confirmKey() | translate"
                    (onClick)="confirmed.emit()" />
            </div>
        </u2b-dialog>
    `,
    styles: [`
        :host(.u2b-confirm--warn) ::ng-deep .u2b-dialog-header {
            background-color: #f59e0b;
            color: #fff;
        }
        :host(.u2b-confirm--danger) ::ng-deep .u2b-dialog-header {
            background-color: #dc2626;
            color: #fff;
        }
    `],
})
export class U2bConfirmDialogComponent {

    readonly severity = input<U2bConfirmSeverity>('primary');
    readonly titleKey = input<string>('');
    readonly messageKey = input.required<string>();
    readonly messageParams = input<Record<string, unknown>>({});
    readonly confirmKey = input<string>('common.confirmAction');
    readonly cancelKey = input<string>('common.cancel');
    // Optional middle action (e.g. "save without sending"). When unset the dialog
    // stays binary (Cancel/Confirm) — existing callers are unaffected.
    readonly declineKey = input<string | null>(null);
    readonly icon = input<string | null>(null);

    readonly confirmed = output<void>();
    readonly cancelled = output<void>();
    readonly declined = output<void>();

    readonly confirmSeverity = computed<'warn' | 'danger' | undefined>(() => {
        switch (this.severity()) {
            case 'warn': return 'warn';
            case 'danger': return 'danger';
            default: return undefined;
        }
    });
}
